/**
 * AddCategoryModal Component
 *
 * Modal for creating a new category from the Categories page (the header
 * "+" button was previously dead - it had no handler at all).
 *
 * - Type preset from the active Expenses/Income tab, switchable via chips
 * - Optional icon picker; "Auto" leaves icon unset so the shared
 *   keyword matcher (utils/categoryIcons.ts) picks one from the name
 * - Non-blocking duplicate-name warning (mirrors AddAccountModal)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Input } from '@/components/shared';
import { categoryService } from '@services/categories';
import { db, Category, CategoryType } from '@services/db';
import { getCategoryIcon } from '@/utils/categoryIcons';
import { useLanguage } from '@/i18n';
import './AddCategoryModal.css';

interface AddCategoryModalProps {
  isOpen: boolean;
  initialType: CategoryType;
  onClose: () => void;
  onSuccess?: () => void;
}

// Curated picker icons (UI decoration; stored on the category when chosen)
const PICKER_ICONS = [
  '🍔', '🛒', '🍽️', '☕', '🏠', '💡', '📄', '🚗',
  '✈️', '🛍️', '👕', '🎬', '🎮', '📱', '💊', '🏋️',
  '📚', '🐾', '🎁', '💼', '🧾', '🪙', '📈', '💰',
];

export function AddCategoryModal({
  isOpen,
  initialType,
  onClose,
  onSuccess,
}: AddCategoryModalProps) {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [type, setType] = useState<CategoryType>(initialType);
  const [icon, setIcon] = useState<string | null>(null); // null = auto
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when opened + load existing categories for the dup warning
  const [existingCategories, setExistingCategories] = useState<Category[]>([]);
  useEffect(() => {
    if (isOpen) {
      setName('');
      setType(initialType);
      setIcon(null);
      setError(null);
      db.categories
        .toArray()
        .then(setExistingCategories)
        .catch(() => setExistingCategories([]));
    }
  }, [isOpen, initialType]);

  const duplicateName = useMemo(() => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return null;
    const match = existingCategories.find(
      (c) => c.name.trim().toLowerCase() === trimmed
    );
    return match ? match.name : null;
  }, [name, existingCategories]);

  // Live preview of what "Auto" would resolve to for the typed name
  const autoIcon = getCategoryIcon(name.trim() || undefined);
  const previewIcon = icon ?? autoIcon;

  const handleSubmit = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('categories.add.err_name_required'));
      return;
    }
    if (trimmed.length > 30) {
      setError(t('categories.add.err_name_long'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      await categoryService.create({
        name: trimmed,
        type,
        icon: icon ?? undefined,
        isActive: true,
        createdAt: now,
        updatedAt: now,
      });
      console.log('[AddCategory] Created category:', trimmed, type);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('categories.add.err_create_failed'));
      console.error('[AddCategory] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [name, type, icon, onSuccess, onClose, t]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('categories.add.title')} size="md">
      <div className="add-category-form">
        {/* Live preview + name */}
        <div className="catadd-name-row">
          <div className="catadd-preview" aria-hidden="true">
            {previewIcon}
          </div>
          <div className="catadd-name-input">
            <Input
              label={t('categories.add.name_label')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder={t('categories.add.name_placeholder')}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
                if (e.key === 'Escape') onClose();
              }}
            />
          </div>
        </div>
        {duplicateName && (
          <div className="dup-warning">
            {t('categories.add.dup_warning', { name: duplicateName })}
          </div>
        )}

        {/* Type chips */}
        <div className="type-chip-group">
          <span className="type-chip-label">{t('categories.add.type_label')}</span>
          <div className="type-chip-grid catadd-type-grid">
            <button
              type="button"
              className={`type-chip ${type === 'expense' ? 'active' : ''}`}
              onClick={() => setType('expense')}
            >
              <span className="type-chip-icon">📤</span>
              <span>{t('common.expenses')}</span>
            </button>
            <button
              type="button"
              className={`type-chip ${type === 'income' ? 'active' : ''}`}
              onClick={() => setType('income')}
            >
              <span className="type-chip-icon">📥</span>
              <span>{t('common.income')}</span>
            </button>
          </div>
        </div>

        {/* Icon picker - Auto chip first, then curated emojis */}
        <div className="type-chip-group">
          <span className="type-chip-label">{t('categories.add.icon_label')}</span>
          <div className="catadd-icon-grid">
            <button
              type="button"
              className={`catadd-icon-btn catadd-icon-auto ${icon === null ? 'active' : ''}`}
              onClick={() => setIcon(null)}
              title={t('categories.add.icon_auto')}
            >
              {t('categories.add.icon_auto')}
            </button>
            {PICKER_ICONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className={`catadd-icon-btn ${icon === emoji ? 'active' : ''}`}
                onClick={() => setIcon(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-buttons">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? t('categories.add.creating') : t('categories.add.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
